import { Component, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { ResetPasswordDto } from 'src/modules/enaio/shared/ResetPasswordDto';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from "src/modules/utils/shared/objects";
import { Utils } from 'src/modules/utils/shared/utils';
import { RestEndpoint } from '../../models/rest-endpoint.model';
import { FrontendField } from '../../netgen/FrontendField';
import { FrontendFieldType } from '../../netgen/FrontendFieldType';
import { FrontendForm } from '../../netgen/FrontendForm';
import { BackendResponse } from '../../shared/backend-response.model';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false
})
export class ResetPasswordComponent extends ComponentView {

    message = inject(MessageService);

    form1: FrontendForm;
    form2: FrontendForm;
    recoveryQuestions: string[];

    userName = null;
    running = false;
    done = false;

    async initParams(): Promise<boolean> {
        this.form1 = new FrontendForm([
            new FrontendField("userName", "Benutzername" , FrontendFieldType.string, { mandatory: true}),
            new FrontendField("send", this.userName == null ? "Weiter" : "Abschließen", FrontendFieldType.button, { 
                submitButton: true,
                icon: "fas fa-check",
                severity: "success",
                onClick: this.send.bind(this)
            }),
            new FrontendField("cancel", "Abbrechen", FrontendFieldType.button, {
                icon: "fas fa-times",
                severity: "danger",
                onClick: this.cancel.bind(this)
            })
        ]);
        return Promise.resolve(true);
    }

    async send(): Promise<void> {
        if (this.running) {
            return;
        }

        if (this.userName == null) {
            this.running = true;
            try {
                let errors = this.form1.validate();
                if (!Utils.isNoe(errors)) {
                    this.message.add({ severity: "error", summary: "Ungültige Angaben", life: 10000, detail: errors[0].message});
                    return;
                }

                if (this.app.mainConfig.authRules.revokePasswordPerEmailIfSet) {
                    if ((await this.app.genericDataHandler(async () => RestEndpoint.main().post().body(Objects.fromPlain(ResetPasswordDto, {
                        userName: this.form1.getValueString("userName")
                    })).run("api/app/auth/resetPassword").getText(), "Fehler", "dialog")).ok) {
                        this.message.add({ severity: "success", summary: "Ein Link zum Zurücksetzen des Passworts wurde an Ihre EMail-Adresse verschickt"});
                        Utils.setTimerOnce(3000, async () => {
                            await this.app.navigateTo(["/base", "login"]);
                        });
                    }
                }
                else {                
                    try {
                        this.recoveryQuestions = await RestEndpoint.main().find({userName: this.form1.getValueString("userName")}).run("api/app/auth/recoveryquestions").listStrings();
                    }
                    catch {
                        this.message.add({ severity: "error", summary: "Der Benutzername ist nicht bekannt"});
                        return;
                    }
                    let userName = this.form1.getValueString("userName");

                    if (this.app.mainConfig.authRules.canRevokePasswordSelf) {
                        this.userName = userName;
                        this.form2 = new FrontendForm([
                            new FrontendField("userName", "Benutzername", FrontendFieldType.string, { mandatory: true, enabled: false, value: this.userName}),
                            new FrontendField("password", "Neues Passwort", FrontendFieldType.password, { mandatory: true, passwordFeedback: false}),
                            new FrontendField("passwordRepeat", "Neues Passwort wiederholen", FrontendFieldType.password, { mandatory: true, passwordFeedback: false}),
                            ...Arrays.mapWithIndex(this.recoveryQuestions, (aq, index) => new FrontendField("authQuestion" + index, aq, FrontendFieldType.string, { mandatory: true }))
                        ]);
                    }
                    else if (await this.app.dialog.yesNo(!Utils.isNoe(this.app.mainConfig.authRules.revokePasswordMessage) ? this.app.mainConfig.authRules.revokePasswordMessage : "Soll das Passwort zurückgesetzt werden?", "Bestätigung")) {
                        await RestEndpoint.main().post().body(Objects.fromPlain(ResetPasswordDto, {
                            userName
                        })).run("api/app/auth/resetpassword").get(BackendResponse);
                        this.message.add({ severity: "success", summary: !Utils.isNoe(this.app.mainConfig.authRules.revokePasswordAfterMessage) ? this.app.mainConfig.authRules.revokePasswordAfterMessage : "Das Passwort wurde erfolgreich zurückgesetzt.", life: 10000});
                        Utils.setTimerOnce(3000, async () => {
                            await this.app.navigateTo(["/base", "login"]);
                        });
                    }
                }
            }
            finally {
                this.running = false;
            }
        }
        else {
            let errors = this.form2.validate();
            if (!Utils.isNoe(errors)) {
                this.message.add({ severity: "error", summary: "Ungültige Angaben", life: 10000, detail: errors[0].message });
                return;
            }

            let pw1 = this.form2.getValueString("password");
            let pw2 = this.form2.getValueString("passwordRepeat");

            if (pw1 != pw2) {
                this.message.add({ severity: "error", summary: "Ungültige Angaben", life: 10000, detail: "Die beiden neuen Passwörter stimmen nicht überein"});
                return;
            }

            if (!this.app.mainConfig.authRules.passwordRules.isSafe(pw1)) {
                this.message.add({ severity: "error", summary: "Ungültige Angaben", life: 10000, detail: "Das neue Passwort stimmt nicht mit den Vorgaben überein. Es muss mindestens 8 Zeichen lang sein und mindestens einen Großbuchstaben, einen Kleinbuchstaben, eine Ziffer und ein Sonderzeichen enthalten"});
                return;
            }

            this.running = true;
            try {
                await RestEndpoint.main().post().body(Objects.fromPlain(ResetPasswordDto, {
                    userName: this.userName,
                    password: pw1,
                    recoveryAnswers: Utils.getRange(0, this.recoveryQuestions.length - 1).map(index => this.form2.getValueString("authQuestion" + index))
                })).run("api/app/auth/resetpassword").get(BackendResponse);
                this.message.add({ severity: "success", summary: "Das Passwort wurde erfolgreich zurückgesetzt.", life: 10000});
                Utils.setTimerOnce(2000, async () => {
                    await this.app.navigateTo(["/base", "login"]);
                });
            }
            catch {
                this.message.add({ severity: "error", summary: "Mindestens eine der Fragen wurde nicht richtig beantwortet", life: 10000});
            }
            this.running = false;
        }
    }

    async cancel(): Promise<void> {
        await this.app.navigateTo(["/base", "login"]);
    }

}